import { normalizeFurniture, type Furniture } from './furniture.js';
export type FloorPlanElementKind = 'wall' | 'room' | 'door' | 'window';
export type Confidence = 'unknown' | 'assumed' | 'confirmed';
export const MAX_FLOORPLAN_CORRECTION_LENGTH = 1000;
export type FloorPlanSurfaceMode = 'pastel' | 'textured' | 'plain';

/** A single normalized rectangle used to select one floor-plan excerpt. */
export interface FloorPlanCropRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FloorPlanPoint { x: number; y: number }

export interface FloorPlanSegment {
  id: string;
  kind: 'wall' | 'door' | 'window';
  start: FloorPlanPoint;
  end: FloorPlanPoint;
  confidence: Confidence;
}

export interface FloorPlanRoom {
  id: string;
  kind: 'room';
  polygon: FloorPlanPoint[];
  label: string;
  confidence: Confidence;
}

export type FloorPlanElement = FloorPlanSegment | FloorPlanRoom;

export interface FloorPlanScale {
  pixelDistance: number | null;
  knownDistanceMeters: number | null;
  confidence: Confidence;
}

export interface FloorPlanProject {
  version: 1;
  backgroundDataUrl: string | null;
  backgroundName: string | null;
  backgroundVisible: boolean;
  elements: FloorPlanElement[];
  furniture?: Furniture[];
  scale: FloorPlanScale;
  review: { threeDReviewed: boolean; reviewedAt: string | null };
  assumptions: { wallHeight: number; wallThickness: number; confidence: 'assumed' | 'confirmed' };
}

export interface RoomAreaResult {
  elementId: string;
  label: string;
  squareMeters: number | null;
  basis: string;
}

export function createFloorPlanProject(): FloorPlanProject {
  return {
    version: 1,
    backgroundDataUrl: null,
    backgroundName: null,
    backgroundVisible: true,
    elements: [],
    furniture: [],
    scale: { pixelDistance: null, knownDistanceMeters: null, confidence: 'unknown' },
    review: { threeDReviewed: false, reviewedAt: null },
    assumptions: { wallHeight: 2.5, wallThickness: 0.18, confidence: 'assumed' },
  };
}

export function floorPlanId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function distancePixels(a: FloorPlanPoint, b: FloorPlanPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

export function polygonAreaPixels(polygon: FloorPlanPoint[]): number | null {
  if (polygon.length < 3) return null;
  const first = polygon[0];
  const last = polygon[polygon.length - 1];
  if (!first || !last || first.x !== last.x || first.y !== last.y) return null;
  let area = 0;
  for (let index = 0; index < polygon.length - 1; index += 1) {
    const current = polygon[index];
    const next = polygon[index + 1];
    if (!current || !next) continue;
    area += current.x * next.y - next.x * current.y;
  }
  return Math.abs(area) / 2;
}

export function calculateRoomAreas(project: FloorPlanProject): RoomAreaResult[] {
  const hasScale = project.scale.confidence === 'confirmed'
    && project.scale.pixelDistance !== null
    && project.scale.pixelDistance > 0
    && project.scale.knownDistanceMeters !== null
    && project.scale.knownDistanceMeters > 0;
  const pixelsPerMeter = hasScale
    ? project.scale.pixelDistance! / project.scale.knownDistanceMeters!
    : null;
  return project.elements.filter((element): element is FloorPlanRoom => element.kind === 'room').map((room) => {
    const pixels = polygonAreaPixels(room.polygon);
    if (!hasScale || pixels === null) {
      return { elementId: room.id, label: room.label || 'Unbenannter Raum', squareMeters: null, basis: 'Unbekannt: geschlossene Kontur und bestätigter Massstab erforderlich.' };
    }
    const squareMeters = pixels / (pixelsPerMeter! * pixelsPerMeter!);
    return {
      elementId: room.id,
      label: room.label || 'Unbenannter Raum',
      squareMeters,
      basis: `Berechnet aus geschlossener Kontur (${Math.round(pixels)} px²) und bestätigtem Massstab (${project.scale.knownDistanceMeters} m / ${Math.round(project.scale.pixelDistance!)} px). Keine amtliche Flächenberechnung.`,
    };
  });
}

export function markFloorPlanEdited(project: FloorPlanProject): FloorPlanProject {
  return { ...project, review: { threeDReviewed: false, reviewedAt: null } };
}

export function normalizeFloorPlanProject(value: unknown): FloorPlanProject {
  if (!value || typeof value !== 'object') throw new Error('Das Grundrissprojekt ist ungültig.');
  const source = value as Partial<FloorPlanProject>;
  if (source.version !== 1 || !Array.isArray(source.elements)) throw new Error('Das Grundrissprojekt hat ein unbekanntes Format.');
  const base = createFloorPlanProject();
  const project: FloorPlanProject = {
    ...base,
    backgroundDataUrl: typeof source.backgroundDataUrl === 'string' ? source.backgroundDataUrl : null,
    backgroundName: typeof source.backgroundName === 'string' ? source.backgroundName : null,
    backgroundVisible: source.backgroundVisible !== false,
    elements: source.elements as FloorPlanElement[],
    furniture: normalizeFurniture(source.furniture),
    scale: { ...base.scale, ...(source.scale || {}) },
    review: { ...base.review, ...(source.review || {}) },
    assumptions: { ...base.assumptions, ...(source.assumptions || {}) },
  };
  for (const element of project.elements) {
    if (!element || !['wall', 'room', 'door', 'window'].includes(element.kind)) throw new Error('Das Grundrissprojekt enthält ein ungültiges Element.');
  }
  return project;
}

/** Explicit manual room names only; unknown use stays neutral. */
export function floorPlanRoomColour(label: string): string {
  const name=label.trim().toLocaleLowerCase('de-CH');
  if (/^(wohnzimmer|schlafzimmer|kinderzimmer|esszimmer|büro|arbeitszimmer|wohnen|schlafen)$/.test(name)) return '#F1E9DD';
  if (/^(küche|bad|badezimmer|wc|dusche)$/.test(name)) return '#E5EDF0';
  if (/^(flur|korridor|entrée|nebenraum|abstellraum|keller|technik)$/.test(name)) return '#ECE9E2';
  return '#EEEDE8';
}
